import React, { useEffect, Suspense, lazy } from "react";
import Footer from "./components/Footer";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CookiePolicy from "./pages/CookiePolicy";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from './auth/AuthContext';
import Header from "./components/Header";
import AuthenticatedRoute from "./components/AuthenticatedRoute";
import ScrollToTop from "./components/ScrollToTop";
import CookieConsent from "./components/CookieConsent";
import { initGA, trackPageView, isGALoaded } from "./utils/analytics";

// Lazy load all page components for code-splitting
const LandingPage = lazy(() => import("./pages/LandingPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AdminTasks = lazy(() => import("./pages/AdminTasks"));
const AdminPractice = lazy(() => import("./pages/AdminPractice"));
const AdminGoals = lazy(() => import("./pages/AdminGoals"));
const AdminModeration = lazy(() => import("./pages/AdminModeration"));
const AdminGamification = lazy(() => import("./pages/AdminGamification"));
const AdminCorrespondence = lazy(() => import("./pages/admin/AdminCorrespondence"));
const ActiveLessons = lazy(() => import("./pages/ActiveLessons"));
const PracticeHome = lazy(() => import("./pages/PracticeHome"));
const PracticeGrade5 = lazy(() => import("./pages/PracticeGrade5"));
const PracticeGrade9 = lazy(() => import("./pages/PracticeGrade9"));
const PracticeSession = lazy(() => import("./pages/PracticeSession"));
const DiagnosticHome = lazy(() => import("./pages/DiagnosticHome"));
const DiagnosticQuick = lazy(() => import("./pages/DiagnosticQuick"));
const DiagnosticClassic = lazy(() => import("./pages/DiagnosticClassic"));
const DiagnosticExtended = lazy(() => import("./pages/DiagnosticExtended"));
const RecommendedLessons = lazy(() => import("./pages/RecommendedLessons"));
const EloEndless = lazy(() => import("./pages/EloEndless"));
const LiveSupport = lazy(() => import("./pages/LiveSupport"));
const QuestionDetail = lazy(() => import("./pages/QuestionDetail"));
const ChatPage = lazy(() => import("./pages/ChatPage"));
const Settings = lazy(() => import("./pages/Settings"));
const Profile = lazy(() => import("./pages/Profile"));
const Premium = lazy(() => import("./pages/Premium"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const AdaptiveDiagnosticsPage = lazy(() => import("./pages/AdaptiveDiagnosticsPage"));
const AboutFeedback = lazy(() => import("./pages/AboutFeedback"));
const AboutLive = lazy(() => import("./pages/AboutLive"));
const AboutProgress = lazy(() => import("./pages/AboutProgress"));
const AboutGamification = lazy(() => import("./pages/AboutGamification"));
const PersonalizationPage = lazy(() => import("./pages/PersonalizationPage"));
const PlatformAboutPage = lazy(() => import("./pages/PlatformAboutPage"));
const PlatformHub = lazy(() => import("./pages/PlatformHub"));
const FeaturesPage = lazy(() => import("./pages/FeaturesPage"));
const PripravaPage = lazy(() => import("./pages/PripravaPage"));
const DemoLogin = lazy(() => import("./pages/DemoLogin"));
const DemoInfo = lazy(() => import("./pages/DemoInfo"));
const SchoolHub = lazy(() => import("./pages/SchoolHub"));
const SchoolPlaceholder = lazy(() => import("./pages/SchoolPlaceholder"));
const SchoolLogin = lazy(() => import("./pages/SchoolLogin"));
const SchoolSignup = lazy(() => import("./pages/SchoolSignup"));
const SchoolDiagnosticLanding = lazy(() => import("./pages/SchoolDiagnosticLanding"));
const SchoolDiagnosticTest = lazy(() => import("./pages/SchoolDiagnosticTest"));
const SchoolStatistics = lazy(() => import("./pages/SchoolStatistics"));
const SchoolDashboard = lazy(() => import("./pages/SchoolDashboard"));
const SchoolPractice = lazy(() => import("./pages/SchoolPractice"));
const TeacherDashboard = lazy(() => import("./pages/TeacherDashboard"));
const AdminSchools = lazy(() => import("./pages/admin/AdminSchools"));

// Loading fallback component
const LoadingFallback = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    minHeight: '100vh',
    fontSize: '1.2rem',
    color: 'var(--text-primary, #e0e0e0)'
  }}>
    Načítání...
  </div>
);

// ProtectedRoute - pro admin-only stránky
function ProtectedRoute({ children }){
  const { current } = useAuth();
  if (!current) return <Navigate to="/" replace />;
  return children;
}

// Komponenta pro podmíněné zobrazení Headeru
function ConditionalHeader() {
  const location = useLocation();
  
  // Skrýt header na landing page, nových public stránkách a školních stránkách
  if (
    location.pathname === '/' || 
    location.pathname === '/o-platforme' || 
    location.pathname === '/funkce' || 
    location.pathname === '/demo-login' ||
    location.pathname.startsWith('/skola/')
  ) {
    return null;
  }
  
  return <Header />;
}

// Analytics wrapper - sleduje změny stránek
function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    // Trackování page views při změně URL
    if (isGALoaded()) {
      trackPageView(location.pathname + location.search, document.title);
    }
  }, [location]);

  return null;
}

export default function App() {
  // Inicializace Google Analytics při startu aplikace
  useEffect(() => {
    const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
    const debugMode = process.env.REACT_APP_GA_DEBUG === 'true';
    const isDev = process.env.REACT_APP_ENVIRONMENT === 'development';
    const consent = localStorage.getItem('cookie_consent');

    if (consent === 'true' && measurementId && !isDev) {
      initGA(measurementId, debugMode);
    }

    const handleConsentChange = (event) => {
      if (event.detail?.consent === 'accepted' && measurementId && !isDev) {
        initGA(measurementId, debugMode);
      }
    };

    window.addEventListener('cookieConsentChanged', handleConsentChange);

    return () => {
      window.removeEventListener('cookieConsentChanged', handleConsentChange);
    };
  }, []);

  return (
    <AuthProvider>
      <ScrollToTop />
      <AnalyticsTracker />
      <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        <ConditionalHeader />
        <div className="app-container" style={{ flex: 1 }}>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* Veřejné stránky */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/ochrana-udaju" element={<PrivacyPolicy />} />
              <Route path="/cookies" element={<CookiePolicy />} />
              {/* O platformě (detailní stránka) */}
              <Route path="/o-platforme" element={<PlatformAboutPage />} />
              {/* Rozcestník témat o platformě */}
              <Route path="/o-platforme/rozcestnik" element={<PlatformHub />} />
              <Route path="/funkce" element={<FeaturesPage />} />
              <Route path="/priprava-na-prijimacky" element={<PripravaPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/jak-to-funguje" element={<AdaptiveDiagnosticsPage />} />
              <Route path="/about/adaptive" element={<Navigate to="/jak-to-funguje" replace />} />
              <Route path="/about/feedback" element={<AboutFeedback />} />
              <Route path="/about/live" element={<AboutLive />} />
              <Route path="/about/progress" element={<AboutProgress />} />
              <Route path="/about/gamification" element={<AboutGamification />} />
              <Route path="/demo-login" element={<DemoLogin />} />
              <Route path="/demoinfo" element={<AuthenticatedRoute><DemoInfo /></AuthenticatedRoute>} />

              {/* Chráněné stránky - vyžadují přihlášení */}
              <Route path="/dashboard" element={<AuthenticatedRoute><Dashboard /></AuthenticatedRoute>} />
              <Route path="/lessons/active" element={<AuthenticatedRoute><ActiveLessons /></AuthenticatedRoute>} />
              <Route path="/lessons/recommended" element={<AuthenticatedRoute><RecommendedLessons /></AuthenticatedRoute>} />
              <Route path="/practice" element={<AuthenticatedRoute><PracticeHome /></AuthenticatedRoute>} />
              <Route path="/practice/5" element={<AuthenticatedRoute><PracticeGrade5 /></AuthenticatedRoute>} />
              <Route path="/practice/9" element={<AuthenticatedRoute><PracticeGrade9 /></AuthenticatedRoute>} />
              <Route path="/practice/session" element={<AuthenticatedRoute><PracticeSession /></AuthenticatedRoute>} />
              <Route path="/practice/elo-endless" element={<AuthenticatedRoute><EloEndless /></AuthenticatedRoute>} />
              <Route path="/diagnostic" element={<AuthenticatedRoute><DiagnosticHome /></AuthenticatedRoute>} />
              <Route path="/diagnostic/:grade/quick" element={<AuthenticatedRoute><DiagnosticQuick /></AuthenticatedRoute>} />
              <Route path="/diagnostic/:grade/classic" element={<AuthenticatedRoute><DiagnosticClassic /></AuthenticatedRoute>} />
              <Route path="/diagnostic/:grade/extended" element={<AuthenticatedRoute><DiagnosticExtended /></AuthenticatedRoute>} />
              <Route path="/live-support" element={<AuthenticatedRoute><LiveSupport /></AuthenticatedRoute>} />
              <Route path="/live-support/question/:id" element={<AuthenticatedRoute><QuestionDetail /></AuthenticatedRoute>} />
              <Route path="/live-support/chat" element={<AuthenticatedRoute><ChatPage /></AuthenticatedRoute>} />
              <Route path="/settings" element={<AuthenticatedRoute><Settings /></AuthenticatedRoute>} />
              <Route path="/profile" element={<AuthenticatedRoute><Profile /></AuthenticatedRoute>} />
              <Route path="/premium" element={<AuthenticatedRoute><Premium /></AuthenticatedRoute>} />
              <Route path="/personalizace" element={<AuthenticatedRoute><PersonalizationPage /></AuthenticatedRoute>} />

              {/* Admin stránky - vyžadují admin roli */}
              <Route path="/admin/tasks" element={<ProtectedRoute><AdminTasks /></ProtectedRoute>} />
              <Route path="/admin/practice" element={<ProtectedRoute><AdminPractice /></ProtectedRoute>} />
              <Route path="/admin/goals" element={<ProtectedRoute><AdminGoals /></ProtectedRoute>} />
              <Route path="/admin/moderation" element={<ProtectedRoute><AdminModeration /></ProtectedRoute>} />
              <Route path="/admin/gamification" element={<ProtectedRoute><AdminGamification /></ProtectedRoute>} />
              <Route path="/admin/correspondence" element={<ProtectedRoute><AdminCorrespondence /></ProtectedRoute>} />
              <Route path="/admin/schools" element={<ProtectedRoute><AdminSchools /></ProtectedRoute>} />

              {/* Školní program - veřejné stránky */}
              <Route path="/skola/:code" element={<SchoolHub />} />
              <Route path="/skola/:code/prihlasit" element={<SchoolLogin />} />
              <Route path="/skola/:code/registrovat" element={<SchoolSignup />} />
              <Route path="/skola/:code/diagnostika" element={<SchoolDiagnosticLanding />} />
              <Route path="/skola/:code/diagnostika/test" element={<SchoolDiagnosticTest />} />
              <Route path="/skola/:code/statistiky" element={<SchoolStatistics />} />
              <Route path="/skola/:code/dashboard" element={<SchoolDashboard />} />
              <Route path="/skola/:code/ucitel/prehled" element={<TeacherDashboard />} />
              <Route path="/skola/:code/pokrok" element={<SchoolPlaceholder type="pokrok" />} />
              <Route path="/skola/:code/procvicovani" element={<SchoolPractice />} />
              <Route path="/skola/:code/procvicovani/session" element={<AuthenticatedRoute><PracticeSession /></AuthenticatedRoute>} />

              {/* 404 */}
              <Route path="/404" element={<NotFound />} />
              <Route path="*" element={<Navigate to="/404" replace />} />
            </Routes>
          </Suspense>
        </div>
        {/* GDPR Cookie Consent Banner */}
        <CookieConsent />
        <Footer />
      </div>
    </AuthProvider>
  );
}
